import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/gameConfig';
import { THEME } from '@/config/theme';
import { drawArcadeBackground, addTextGlow } from '@/utils/arcadeBackground';
import type { Grade } from '@/config/scoring';
import { createButton, setButtonLabel } from '@/ui/buttons';
import { makeQrSvg } from '@/online/qr';
import { getDailyShareUrl } from '@/utils/dailyShare';
import { createDailyShare } from '@/services/dailyShare';
import { syncDailyBest } from '@/services/dailyScores';
import { submitLeaderboardRun } from '@/services/leaderboard';
import { ensureDiscordAccessToken, getDiscordGuildId, ensureDiscordUser, setDiscordDailyResultsPresence } from '@/discord';
import { isDiscordActivity } from '@/utils/discordActivity';
import { getClientVersion } from '@/utils/version';
import { fetchAccountStatus } from '@/services/accountStatus';
import { formatDailyDisplayDate } from '@/utils/dailyDate';
import { AudioManager } from '@/systems/AudioManager';
import { trackPlayAgain } from '@/services/analytics';

interface DailyResultSceneData {
  score: number;
  streak: number;
  decodedMessage: string;
  intendedMessage: string;
  accuracy: number;
  grade: Grade;
  dailyDate: string;
  dailyId: string;
  eliminated: boolean;
}

export class DailyResultScene extends Phaser.Scene {
  private shareUrl: string | null = null;
  private bestText: Phaser.GameObjects.Text | null = null;
  private bestBadge: Phaser.GameObjects.Text | null = null;
  private qrElement: Phaser.GameObjects.DOMElement | null = null;
  private shareLinkInput: Phaser.GameObjects.DOMElement | null = null;
  private nameText: Phaser.GameObjects.Text | null = null;

  constructor() {
    super({ key: 'DailyResultScene' });
  }

  create(data: DailyResultSceneData): void {
    // Discord Activity presence
    setDiscordDailyResultsPresence(data.score);

    this.cameras.main.fadeIn(300, 5, 7, 22);
    drawArcadeBackground(this);

    const normalizedDecoded = data.decodedMessage.replace(/\s+/g, '');
    const normalizedTarget = data.intendedMessage.replace(/\s+/g, '');
    const success = !data.eliminated && normalizedDecoded === normalizedTarget;

    const card = this.add.rectangle(GAME_WIDTH / 2, 160, 560, 190, THEME.colors.backgroundLightHex, 0.7);
    card.setStrokeStyle(2, THEME.colors.backgroundPanelHex);
    card.setDepth(-1);

    this.add.text(GAME_WIDTH / 2, 100, `${data.score.toLocaleString()} PTS`, {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.score,
      color: THEME.colors.yellow,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, 160, formatDailyDisplayDate(data.dailyDate), {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
    }).setOrigin(0.5);

    this.nameText = this.add.text(GAME_WIDTH / 2, 205, 'Anonymous', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);

    const transmittedHeaderY = 290;
    this.add.text(GAME_WIDTH / 2, transmittedHeaderY, 'TRANSMITTED:', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, transmittedHeaderY + 12, data.decodedMessage, {
      fontFamily: THEME.fonts.mono,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      wordWrap: {
        width: GAME_WIDTH / 2,
        useAdvancedWrap: true,
      },
      align: 'center',
      maxLines: 3,
    }).setOrigin(0.5, 0).setAlpha(0.8);

    const leftColumnX = GAME_WIDTH / 2 - 300;
    const middleColumnX = GAME_WIDTH / 2;
    const rightColumnX = GAME_WIDTH / 2 + 300;
    const columnY = 400;
    const statGap = 34;

    this.add.text(leftColumnX, columnY, 'ACCURACY', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);

    this.add.text(leftColumnX, columnY + 26, `${data.accuracy.toFixed(2)}%`, {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
    }).setOrigin(0.5);

    this.add.text(leftColumnX, columnY + statGap * 2, 'GRADE', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);

    this.add.text(leftColumnX, columnY + statGap * 2 + 26, data.grade, {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
    }).setOrigin(0.5);

    this.bestText = this.add.text(leftColumnX, columnY + statGap * 4, 'BEST TODAY', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);

    this.bestBadge = this.add.text(leftColumnX, columnY + statGap * 4 + 26, '—', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
    }).setOrigin(0.5);

    this.add.text(middleColumnX, columnY, 'SHARE', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);

    this.shareLinkInput = this.add.dom(middleColumnX, columnY + 30).createFromHTML(`
      <input
        data-share-input
        type="text"
        readonly
        value="${this.shareUrl ?? 'Generating link...'}"
        style="
          width: 260px;
          padding: 6px 10px;
          text-align: center;
          font-family: ${THEME.fonts.body};
          font-size: ${THEME.sizes.small};
          color: ${THEME.colors.text};
          background: ${THEME.colors.backgroundLight};
          border: 1px solid ${THEME.colors.backgroundPanel};
          border-radius: 6px;
          outline: none;
        "
      />
    `);

    const copyButton = createButton(this, {
      x: middleColumnX,
      y: columnY + 88,
      text: 'COPY LINK',
      style: 'secondary',
      onClick: () => {
        void navigator.clipboard?.writeText(this.shareUrl ?? '');
        setButtonLabel(copyButton, 'COPIED');
        this.time.delayedCall(1200, () => setButtonLabel(copyButton, 'COPY LINK'));
      },
      sizes: {
        secondary: { width: 180, height: 44, fontSize: THEME.sizes.body },
      },
      buttonType: 'normal',
    });
    copyButton.setAlpha(0.4);
    copyButton.iterate((obj: Phaser.GameObjects.GameObject) => {
      if (obj instanceof Phaser.GameObjects.Rectangle) {
        obj.disableInteractive();
      }
    });

    this.qrElement = this.add.dom(rightColumnX, columnY + 70).createFromHTML(
      '<div data-qr style="width: 110px; height: 110px;"></div>'
    );

    void this.refreshShareLink(data, copyButton);

    createButton(this, {
      x: GAME_WIDTH / 2 - 160,
      y: GAME_HEIGHT - 95,
      text: 'PLAY AGAIN',
      style: 'primary',
      buttonType: 'normal',
      onClick: () => {
        trackPlayAgain();
        this.scene.start('DailyChallengeScene');
      },
      sizes: {
        primary: { width: 220, height: 54, fontSize: THEME.sizes.bodyLarge },
      },
    });

    createButton(this, {
      x: GAME_WIDTH / 2 + 160,
      y: GAME_HEIGHT - 95,
      text: 'MAIN MENU',
      style: 'secondary',
      buttonType: 'normal',
      onClick: () => this.scene.start('MenuScene'),
      sizes: {
        secondary: { width: 200, height: 50, fontSize: THEME.sizes.body },
      },
    });

    this.updateBest(data, success);
    this.maybeSubmitLeaderboard(data);
  }

  private updateBest(data: DailyResultSceneData, completed: boolean): void {
    const completedAt = completed ? new Date().toISOString() : null;
    void syncDailyBest(data.dailyDate, {
      date: data.dailyDate,
      score: data.score,
      accuracy: data.accuracy,
      grade: data.grade,
      completedAt,
    }).then((result) => {
      if (!this.bestText || !this.bestBadge) return;
      if (result.isNewBest) {
        AudioManager.get(this).playSfx('high_score');
        this.bestText.setText('NEW DAILY BEST');
        this.bestText.setFontFamily(THEME.fonts.heading);
        this.bestText.setFontSize(THEME.sizes.body);
        this.bestText.setColor(THEME.colors.yellow);
        this.bestBadge?.setVisible(false);
        addTextGlow(this.bestText, THEME.colors.yellow, 10);
      } else {
        this.bestText.setText('BEST TODAY');
        this.bestText.setFontFamily(THEME.fonts.pixel);
        this.bestText.setFontSize(THEME.sizes.small);
        this.bestText.setColor(THEME.colors.textMuted);
        this.bestBadge?.setVisible(true);
        this.bestBadge?.setText(result.best.score.toLocaleString());
        this.bestBadge?.setFontSize(THEME.sizes.bodyLarge);
        this.bestBadge?.setColor(THEME.colors.text);
      }
    });
  }

  private maybeSubmitLeaderboard(data: DailyResultSceneData): void {
    if (!isDiscordActivity()) return;
    const guildId = getDiscordGuildId();
    if (!guildId) return;

    void (async () => {
      const accessToken = await ensureDiscordAccessToken();
      if (!accessToken) return;

      const { hash } = getClientVersion();
      await submitLeaderboardRun({
        guildId,
        leaderboardType: `daily:${data.dailyDate}`,
        score: data.score,
        discordAccessToken: accessToken,
        metadata: {
          accuracy: data.accuracy,
          grade: data.grade,
          maxStreak: data.streak,
          buildHash: hash || 'unknown',
          messageId: data.dailyId,
          dailyDate: data.dailyDate,
        },
      });
    })();
  }

  private async refreshShareLink(data: DailyResultSceneData, copyButton: Phaser.GameObjects.Container): Promise<void> {
    const name = await this.resolveShareName();
    this.updateNameText(name);
    const shareResult = await createDailyShare({
      date: data.dailyDate,
      score: data.score,
      name,
      dailyId: data.dailyId,
    });

    if (!shareResult.id) {
      this.updateShareInputValue(`Share unavailable (${shareResult.error ?? 'error'})`);
      copyButton.setAlpha(0.4);
      return;
    }

    this.shareUrl = getDailyShareUrl(shareResult.id);
    if (!this.shareUrl) {
      this.updateShareInputValue('Share unavailable (invalid link)');
      copyButton.setAlpha(0.4);
      return;
    }

    copyButton.setAlpha(1);
    copyButton.iterate((obj: Phaser.GameObjects.GameObject) => {
      if (obj instanceof Phaser.GameObjects.Rectangle) {
        obj.setInteractive({ useHandCursor: true });
      }
    });
    this.updateShareInputValue(this.shareUrl);

    const qrDiv = this.qrElement?.node.querySelector('[data-qr]') as HTMLDivElement | null;
    if (qrDiv) {
      qrDiv.innerHTML = makeQrSvg(this.shareUrl) ?? '';
    }
  }

  private async resolveShareName(): Promise<string | null> {
    if (isDiscordActivity()) {
      const user = await ensureDiscordUser();
      const name = user?.globalName || user?.username || null;
      return name ?? null;
    }

    const status = await fetchAccountStatus();
    return status.state === 'signed_in' ? status.email : null;
  }

  private updateNameText(name: string | null): void {
    if (!this.nameText) return;
    const label = name?.trim() || 'Anonymous';
    this.nameText.setText(label);
  }

  private updateShareInputValue(value: string): void {
    const input = this.shareLinkInput?.node.querySelector('[data-share-input]') as HTMLInputElement | null;
    if (input) {
      input.value = value;
    }
  }
}
